import ks from "../ReUsableTS/kBrowserShared";
import { kDiv,khtmlButton} from "../ReUsableTS/kHTML";
import { kMotion } from "./kMotion";
import LNG from "./kLNG";
import { kStyleClass } from "./kStyles";
import { kFormTheme } from "./kFormTheme";
import { kScopedStyles } from "./kScopedStyles";
import { kModalDialogStyles } from "./kModalDialogStyles";
import { kModalDialogStatics } from "./kModalDialogStatics";
import { kModalDialogLifecycle } from "./kModalDialogLifecycle";
import { kModalDialogButtons } from "./kModalDialogButtons";
import { kModalDialogContent } from "./kModalDialogContent";
export { kModalDialogStatics, kModalDialogLifecycle, kModalDialogButtons, kModalDialogContent };
// CLASS-SIZE-EXCEPTION: one inseparable concern — the modal-dialog lifecycle
// (build, show, keyboard/focus contract, close, teardown). Splitting the focus
// trap or the keyboard handling into a sibling class would hand a second object
// write access to the same _IsOpen / _KeyHandler / DialogContent state, which
// Rule 1 forbids more strongly than it forbids the line count.
export class kModalDialog{
    /** Optional app-wide hook that lets an open modal participate in the browser history so
     *  the Back button closes it (and closing the modal pops the history entry). Null (the
     *  default) = no history integration, so every existing consumer is unaffected. An app
     *  sets it ONCE at boot — typically to a hash router's OpenModal/CloseModal — and then
     *  every kModalDialog in that app joins the Back stack automatically, no per-dialog wiring.
     *  Open receives THIS dialog's Close so a Back can invoke it; Close is called from every
     *  close path so the pushed history entry is popped. */
    public static HistoryHooks: { Open: (Close: () => void) => void; Close: () => void } | null = null;
    /** Every dialog that is currently open, in the order they were opened. The LAST entry is
     *  the one the keyboard belongs to: Escape and the Tab focus trap only act for the topmost
     *  dialog, so a confirm/error dialog raised ON TOP of a form dialog no longer closes both
     *  with one Escape (each instance installs its own document-level keydown listener, and
     *  stopPropagation does not stop siblings on the same node). */
    private static _OpenStack: kModalDialog[] = [];
    /** Observers notified when the modal-open state changes. Called with `true` while at
     *  least one dialog is open and `false` once the last one closes — so a heavy background
     *  surface the modal fully covers (e.g. a live WebGL map that keeps repainting) can pause
     *  itself, freeing the compositor to present the modal's frame, then resume on close.
     *  Additive: no observers by default → no behaviour change for existing consumers. */
    private static _OpenStateObservers: Array<(AnyOpen: boolean) => void> = [];
    /** Monotonic counter behind the per-instance element ids. Never reset: a closed dialog may
     *  still be in the DOM (see AutoRemoveMs), so reusing a number would recreate the duplicate
     *  id it exists to avoid. */
    private static _InstanceCount: number = 0;
    /**
     * Milliseconds between Close() and the automatic DETACH of this dialog's container from
     * `document.body`. 2000 by default; 0 turns the whole mechanism off.
     *
     * WHY THIS EXISTS. A closed kModalDialog used to stay in the document for ever, because
     * DestroyOnClose defaults false so the same instance can be re-Show()n. The consequence was
     * not tidiness - it was a class of silent, repeatedly-rediscovered defects, one per surface
     * that looks the DOM up globally:
     *   - `document.querySelector(".kDialogContent")` returns the FIRST match in document order,
     *     which is the OLDEST dead dialog, not the one on screen (CoreTenantSecurity 2026-05-23);
     *   - a Playwright `waitForSelector` resolves instantly against that same corpse and then
     *     waits for ever for it to become visible (Templates Manager 2026-08-06);
     *   - every id inside the content is duplicated the moment a second dialog exists.
     * Each was found separately, in a different product, and each cost a debugging session.
     *
     * DETACH, NOT DESTROY, and that distinction is the whole design. `Destroy()` tears down
     * listeners and empties the node; this only takes the container OUT of `document.body` and
     * leaves the object graph untouched, so `Show()` re-attaches it and every existing re-Show
     * consumer is unaffected. Node identity never changes, wired handlers survive, and a caller
     * holding `DialogContent` can still populate it while the dialog is off-document.
     *
     * WHY A DELAY AT ALL rather than removing inside Close(). Close() is called from the middle
     * of a click/keydown handler, and an in-flight save callback, a CSS close transition or a
     * consumer's own OnClosedCB may still touch nodes inside the dialog on the next few frames.
     * Detaching synchronously would pull the tree out from under them. 2 s is far longer than any
     * of those and far shorter than the "user opens the next dialog" gesture it must beat.
     *
     * The timer is CANCELLED by Show() (a re-open inside the window keeps the attached node) and
     * by Destroy(), so no dialog is ever detached while it is on screen.
     */
    public static AutoRemoveMs: number = 2000;
    /** Handle of the pending auto-detach, 0 when none is armed. */
    private _RemoveTimer: number = 0;
    /** Everything that can hold focus inside the dialog box. Disabled controls are excluded so
     *  the trap never parks focus on a dead button; tabindex="-1" nodes (including the dialog
     *  box itself) are programmatic-focus targets only and never part of the Tab cycle. */
    private static _FocusableSelector: string =
        "a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), "
        + "textarea:not([disabled]), [tabindex]:not([tabindex=\"-1\"])";
    /** True once this dialog registered an OPEN with HistoryHooks — so Close only fires the
     *  matching HistoryHooks.Close (and never double-registers across a re-Show). */
    private _RegisteredHistory: boolean = false;

    public DestroyOnClose:boolean = false;
    // When true, a click on the dark backdrop (the container, outside the dialog box)
    // closes the dialog — the standard "click-away to dismiss" affordance. Default
    // false preserves the original behaviour for every existing caller (and forms /
    // error dialogs that must not be dismissed by a stray click). A read-only detail
    // dialog opts in by setting this true before Show().
    public CloseOnBackdropClick:boolean = false;

    // ── Open state ──────────────────────────────────────────────────────────
    // The state lives in ONE private field. `IsOpen` is the canonical PascalCase
    // name; `isOpen` is kept as a same-state alias because renaming a public
    // property is a breaking change for ICD / A3FormsBuilder / Vinspec / the
    // kEditable* family, all of which read and write the camelCase name today.
    private _IsOpen: boolean = false;
    /** True while the dialog is on screen. */
    public get IsOpen(): boolean { return this._IsOpen; }
    public set IsOpen(Value: boolean) { this._IsOpen = Value; }
    /** Legacy alias for {@link IsOpen} — identical state, kept for existing callers. */
    public get isOpen(): boolean { return this._IsOpen; }
    public set isOpen(Value: boolean) { this._IsOpen = Value; }

    /**
     * The backdrop element's class name, as a CONSTANT rather than a literal typed twice.
     *
     * It was already a contract — the stylesheet's reduced-motion rules match on it — and it
     * became one other code has to honour when a product started hosting this library inside a
     * page it hides: the Templates Manager task pane blanks the SharePoint page around it and
     * has to spare every shared modal, INCLUDING THE ERROR DIALOG, which is built from this
     * class. Renaming the string without this constant would take the product's only error
     * surface off the screen in that host, silently.
     */
    public static readonly ContainerClass: string = "kDialogContainer";
    /** The dialog's own font stack (kModalDialogStyles.FontFamily, which Preinstall warms). */
    private static readonly _FontFamily: string = kModalDialogStyles.FontFamily;

    public DialogContainer: HTMLDivElement = document.createElement("div");
    public DialogContent: HTMLDivElement = document.createElement("div");
    public InnerContent: HTMLDivElement = document.createElement("div");
    public Height: number = 0;
    public Width: number = 0;
    public Top:number = -1; //Centered

    public Title: string = "";
    public TitleDiv: HTMLDivElement = document.createElement("div");
    /** id of TitleDiv — the target of the dialog box's aria-labelledby. */
    private _TitleId: string = "";

    // ── Cancel button ───────────────────────────────────────────────────────
    private _ShowCancelButton: boolean = false;
    /** Show a Cancel button when the dialog opens. */
    public get ShowCancelButton(): boolean { return this._ShowCancelButton; }
    public set ShowCancelButton(Value: boolean) { this._ShowCancelButton = Value; }
    /** Legacy alias for {@link ShowCancelButton} — identical state. */
    public get showCancelButton(): boolean { return this._ShowCancelButton; }
    public set showCancelButton(Value: boolean) { this._ShowCancelButton = Value; }

    public CancelButtonTopRight: HTMLSpanElement = document.createElement("span");
    public OnCancelCB: any = null;
    // Built lazily by AddCancelButton (called from Show when showCancelButton
    // is set) — null until then, so no throwaway <button> is created per dialog.
    public CancelButton:khtmlButton | null = null;
    public CancelButtonCaption:string = "";

    // ── OK button ───────────────────────────────────────────────────────────
    private _ShowOKButton: boolean = false;
    /** Show an OK button when the dialog opens. */
    public get ShowOKButton(): boolean { return this._ShowOKButton; }
    public set ShowOKButton(Value: boolean) { this._ShowOKButton = Value; }
    /** Legacy alias for {@link ShowOKButton} — identical state. */
    public get showOKButton(): boolean { return this._ShowOKButton; }
    public set showOKButton(Value: boolean) { this._ShowOKButton = Value; }

    // Built lazily by AddOKButton (called from Show when showOKButton is set) —
    // null until then, so no throwaway <button> is created per dialog.
    public OKButton: khtmlButton | null = null;
    public OKButtonCaption:string = "";
    public OKCB: any = null;
    /** Opt in for asynchronous OK operations: the caller closes after successful completion. */
    public KeepOpenOnOK: boolean = false;
    // Optional host styling for the OK/Cancel buttons. When ButtonsUseDefaultStyle is
    // left true (the default) the buttons keep the built-in khtmlButton look and these
    // class names are ignored, so every existing caller is unaffected. Set
    // ButtonsUseDefaultStyle = false and give a class name to let a product stylesheet
    // own the button appearance (e.g. brand-consistent primary/secondary buttons).
    public OKButtonClassName: string = "";
    public CancelButtonClassName: string = "";
    /** Set by SetAccentTitle; empty = no dot. Kept so _ApplyTitle can restore the dot
        after it overwrites the title's textContent. */
    private _TitleAccentColor: string = "";
    public ButtonsUseDefaultStyle: boolean = true;
    // Height 0 normally means "80% of the window" - a three-line confirmation then opens as a
    // near-empty sheet. AutoHeight = true (with Height 0) sizes the dialog to its content, capped
    // at 80%. False (the default) keeps every existing caller exactly as it was.
    public AutoHeight: boolean = false;
    // Optional OK-time validator. When set, it runs BEFORE OKCB on every OK path
    // (the OK button and the Enter key): if it returns false the dialog stays
    // open and OKCB does NOT fire, so a form can reject invalid input in place
    // instead of closing silently. Null (the default) keeps the legacy behaviour
    // (OK always commits + closes), so existing callers are unaffected.
    public OKValidateCB: (() => boolean) | null = null;
    // Fired from Close() on EVERY close path (OK, Cancel, Escape, the top-right
    // ×, programmatic Close). Unlike OnCancelCB this is not skipped by the ×,
    // so it is the reliable place to release resources a dialog created (e.g.
    // an embedded editor widget). Null (the default) is a no-op.
    public OnClosedCB: any = null;
    /** Fade-in duration for Show(), in milliseconds. 0 (the default) reproduces
     *  today's instant reveal exactly, so no existing caller sees a change. When
     *  a caller sets it above 0 the backdrop fades in — and the fade is skipped
     *  entirely when the OS reports `prefers-reduced-motion: reduce`. */
    public AnimateOpenMs: number = 0;
    private _ButtonRow: HTMLDivElement | null = null;

    public ContentHTML: string = "";
    private _KeyHandler: ((ev: KeyboardEvent) => void) | null = null;
    private _PreviouslyFocused: HTMLElement | null = null;
    private _FocusTrapHandler: ((ev: KeyboardEvent) => void) | null = null;
    /** Bounded retry counter for _AttemptAddContainer — see that method. */
    private _ContainerAttempts: number = 0;
    /** True once SetHostStyled handed the appearance to a product stylesheet — see that method. */
    private _HostStyled: boolean = false;

    constructor (Title?: string, ContentHTML?: string, onCancelCB?: any, Height?: number, Width?: number, showCancelButton?: boolean,OKButtonFirst?:boolean){
        if (ks.KErrorMode === true) { return; }
        try {
            if(undefined !== Title){
                this.Title = ks.Nz(Title);
            }
            if(undefined !== ContentHTML){
                this.ContentHTML = ks.Nz(ContentHTML);
            }
            if(undefined !== Height){
                this.Height = ks.N0(Height);
            }
            if(undefined !== Width){
                this.Width = ks.N0(Width);
            }
            if(showCancelButton !== undefined){
                this._ShowCancelButton = ks.Nb(showCancelButton);
            }
            if (undefined !== onCancelCB) {
                this.OnCancelCB = onCancelCB;
            }

            this.DialogContainer = new kDiv().Div;
            this.DialogContainer.classList.add(kModalDialog.ContainerClass);
            if(this.DialogContainer===null){return;}
            let CurrStyle: CSSStyleDeclaration = this.DialogContainer.style;

            CurrStyle.display ="none"; /* Hidden by default */
            CurrStyle.position ="fixed"; /* Stay in place */
            // Error and confirm dialogs must always sit above every app
            // overlay (e.g. the ISO first-use overlay at z-index 100000).
            // 2_147_483_640 is just under the 32-bit signed int max so any
            // app overlay that needs to escape only has to stay below this.
            CurrStyle.zIndex = "2147483640"; /* Sit on top */
            CurrStyle.left =  "0px";
            CurrStyle.top =  "0px";
            CurrStyle.width = "100%"; /* Full width */
            CurrStyle.height ="100%"; /* Full height */
            CurrStyle.overflow = "hidden"; /* No scrollbar on overlay */
            CurrStyle.backgroundColor = "rgb(0,0,0)"; /* Fallback color */
            CurrStyle.backgroundColor = "rgba(0,0,0,0.4)"; /* Black w/ opacity */
            CurrStyle.fontFamily = kModalDialog._FontFamily;
            CurrStyle.fontSize = "13px";
            /* Center dialog vertically and horizontally */
            CurrStyle.display = "none";
            CurrStyle.alignItems = "center";
            CurrStyle.justifyContent = "center";
            this.DialogContent = new kDiv().Div;
            this.DialogContent.classList.add("kDialogContent");
            if(this.DialogContent===null){return;}
            this.DialogContent.setAttribute("role", "dialog");
            this.DialogContent.setAttribute("aria-modal", "true");
            // Programmatic-focus target for the empty-dialog case. Without an explicit
            // tabindex, DialogContent.focus() is silently a no-op and focus stays behind
            // the modal on the page underneath. -1 keeps it OUT of the Tab cycle.
            this.DialogContent.tabIndex = -1;
            this.DialogContent.style.position = "relative";
            this.DialogContent.style.display = "flex";
            this.DialogContent.style.flexDirection = "column";
            this.DialogContent.style.overflow = "hidden";
            CurrStyle = this.DialogContent.style;
            CurrStyle.backgroundColor = "#fefefe";
            CurrStyle.margin = "0";
            CurrStyle.padding = "16px";
            CurrStyle.border = "1px solid #d2d0ce";
            CurrStyle.borderRadius = "8px";
            CurrStyle.boxShadow = "0 8px 32px rgba(0,0,0,0.18)";

            this.CancelButtonTopRight = document.createElement("span");
            this.CancelButtonTopRight.className = "kDialogTopRightClose";
            this.CancelButtonTopRight.textContent = "×";
            // The element stays a <span> because CancelButtonTopRight is a PUBLIC field
            // typed HTMLSpanElement (in this file and in the shipped kModalDialog.d.ts) —
            // swapping the tag would break every caller that annotates it. role=button +
            // tabindex=0 + the Enter/Space handler below give it the full button contract
            // the standards require of a non-<button> control.
            this.CancelButtonTopRight.setAttribute("role", "button");
            this.CancelButtonTopRight.setAttribute("aria-label", LNG.CLOSE);
            this.CancelButtonTopRight.setAttribute("title", LNG.CLOSE);
            this.CancelButtonTopRight.setAttribute("tabindex", "0");
            const kCloseStyle:kStyleClass = kModalDialogStyles.CloseStyle();
            kCloseStyle.Apply(this.CancelButtonTopRight);
            kModalDialogStyles.InstallSharedStyles();

            this.CancelButtonTopRight.addEventListener("click", this.Close);
            this.CancelButtonTopRight.addEventListener("keydown", this._OnCloseKeyDown);
            this.DialogContent.appendChild(this.CancelButtonTopRight);

            this.TitleDiv = new kDiv("kDialogTitle").Div;
            this.TitleDiv.style.minHeight = "18px";
            // The kDiv above set id="kDialogTitle"; the aria-labelledby id below then OVERWRITES
            // it, so up to 2026-08-04 neither an id nor a class called kDialogTitle survived on
            // the element and every "#kDialogTitle" / ".kDialogTitle" selector in a consumer or a
            // test matched nothing at all. Carry it as a CLASS, which is what a repeated element
            // needs: ids must be unique per document and a dialog is opened many times per page.
            this.TitleDiv.classList.add("kDialogTitle");
            this._TitleId = "kDlgTitle_" + Date.now() + "_" + Math.floor(Math.random() * 10000);
            this.TitleDiv.id = this._TitleId;
            this.DialogContent.setAttribute("aria-labelledby", this._TitleId);
            if(this.TitleDiv !== null){
                this.DialogContent.appendChild(this.TitleDiv);
            }

            // CLASS for selecting, plus a per-instance ID so the document stays valid.
            //
            // This used to be a bare id="kDialogInnerContent". A CLOSED kModalDialog STAYS IN THE
            // DOM and the next open appends another, so the second dialog on a page made that id
            // a duplicate - at which point document.getElementById and a "#kDialogInnerContent"
            // test selector both silently resolve to the FIRST, long-dead dialog. Measured on
            // CBAudit 2026-08-04. The counter, not Date.now(), because two dialogs built in the
            // same millisecond would otherwise collide again.
            kModalDialog._InstanceCount = kModalDialog._InstanceCount + 1;
            this.InnerContent = new kDiv("kDialogInnerContent_" + kModalDialog._InstanceCount).Div;
            this.InnerContent.classList.add("kDialogInnerContent");
            if(this.InnerContent){
                this.InnerContent.style.flex = "1";
                this.InnerContent.style.minHeight = "0";
                this.InnerContent.style.width = "100%";
                /* Scroll the content area when it is taller than the dialog —
                   the parent .kDialogContent is fixed-height + overflow:hidden,
                   so without this, over-tall content (e.g. a long user list) is
                   clipped behind the OK/Cancel buttons instead of scrolling. */
                this.InnerContent.style.overflowY = "auto";
                this.DialogContent.appendChild(this.InnerContent);
            }
            if (this._ShowCancelButton || this._ShowOKButton) {
                if (OKButtonFirst) {
                    if (this._ShowOKButton) {
                        this.AddOKButton();
                    }
                    if (this._ShowCancelButton) {
                        this.AddCancelButton();
                    }
                } else {
                    if (this._ShowCancelButton) {
                        this.AddCancelButton();
                    }
                    if (this._ShowOKButton) {
                        this.AddOKButton();
                    }
                }
            }
            this.DialogContainer.appendChild(this.DialogContent);
            // Backdrop click-away (opt-in via CloseOnBackdropClick). The listener is on the
            // container; it only closes when the click lands ON the container itself (the
            // dark area), not when it bubbles up from the dialog box or its content.
            this.DialogContainer.addEventListener("click", this._OnBackdropClick);
            this._AttemptAddContainer();

        } catch (e) { ks.KGlobalErrorHandler(e, "kModalDialog.contructor"); }
    }
    // ── error-mode guards in this file ──────────────────────────────────────
    // Most methods below guard with the COMPOUND form
    //     if (ks.KErrorMode && ks.KShowingErrorDialog === false) { return; }
    // instead of the plain `if (ks.KErrorMode) { return; }`. That is deliberate and
    // load-bearing: kModalDialog IS the error handler's own dialog infrastructure
    // (ks.ConfirmYesNo builds an error report on a kModalDialog). Once the error
    // dialog is on screen, ks.KErrorMode is true again — so a plain guard would make
    // its OK/Cancel/Escape/× paths no-ops and the user could never dismiss, or even
    // send, the error report. The second clause is what keeps the handler's own
    // dialog alive while every OTHER dialog is correctly frozen.
    /** Install every stylesheet a dialog needs NOW, without opening one - see kModalDialogStyles.Preinstall.
     *  DOCUMENTED EXCEPTION: transparent dispatch; kModalDialogStyles.Preinstall owns its gate and fault report. */
    public static Preinstall = (): void => kModalDialogStyles.Preinstall();
    private _OnBackdropClick = (ev: MouseEvent): void => kModalDialogLifecycle._OnBackdropClick(this, ev); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogLifecycle._OnBackdropClick carries the guard, try/catch and handler.
    private _OnCloseKeyDown = (ev: KeyboardEvent): void => kModalDialogLifecycle._OnCloseKeyDown(this, ev); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogLifecycle._OnCloseKeyDown carries the guard, try/catch and handler.
    private _AttemptAddContainer = (): void => kModalDialogLifecycle._AttemptAddContainer(this); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogLifecycle._AttemptAddContainer carries the guard, try/catch and handler.
    public Show = (): void => kModalDialogLifecycle.Show(this, kModalDialog); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogLifecycle.Show carries the guard, try/catch and handler.
    private _FitTabStripToContent = (): void => kModalDialogContent._FitTabStripToContent(this); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogContent._FitTabStripToContent carries the guard, try/catch and handler.

    private _ApplyTitle = (): void => kModalDialogContent._ApplyTitle(this); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogContent._ApplyTitle carries the guard, try/catch and handler.
    private _RevealContainer = (): void => kModalDialogLifecycle._RevealContainer(this, kModalDialog); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogLifecycle._RevealContainer carries the guard, try/catch and handler.
    private _StartOpenFade = (): void => kModalDialogLifecycle._StartOpenFade(this); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogLifecycle._StartOpenFade carries the guard, try/catch and handler.
    /** Public so a consumer that animates its own dialog content can follow the
     *  same decision. Routed through kMotion so there is ONE switch for the whole
     *  library: false unless a project opts in, so the dialog animates by default
     *  whatever the OS setting says (product decision 2026-07-31; see the kMotion
     *  class comment in kAnimateTexts.ts). */
    public static PrefersReducedMotion = (): boolean => kModalDialogStatics.PrefersReducedMotion(); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogStatics.PrefersReducedMotion carries the guard, try/catch and handler.
    /** Subscribe to the modal-open state (see _OpenStateObservers). Idempotent — the same
     *  observer is registered once. Pair with RemoveOpenStateObserver in the view's teardown. */
    public static AddOpenStateObserver = (Observer: (AnyOpen: boolean) => void): void => kModalDialogStatics.AddOpenStateObserver(kModalDialog, Observer); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogStatics.AddOpenStateObserver carries the guard, try/catch and handler.
    /** True while at least one dialog is open, anywhere in the app. A surface with its OWN
     *  keyboard handling (a slide-in drawer, an inline editor) asks this before acting on
     *  Escape or Tab, so a dialog raised ON TOP of it keeps the keyboard to itself. Reads the
     *  same stack the topmost-instance rules use, so the two can never disagree. */
    public static AnyOpen = (): boolean => kModalDialogStatics.AnyOpen(kModalDialog); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogStatics.AnyOpen carries the guard, try/catch and handler.
    /** Unsubscribe an observer (call from a view's Destroy so a torn-down surface is never notified). */
    public static RemoveOpenStateObserver = (Observer: (AnyOpen: boolean) => void): void => kModalDialogStatics.RemoveOpenStateObserver(kModalDialog, Observer); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogStatics.RemoveOpenStateObserver carries the guard, try/catch and handler.
    private static _NotifyOpenState = (): void => kModalDialogStatics._NotifyOpenState(kModalDialog); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogStatics._NotifyOpenState carries the guard, try/catch and handler.
    private _RegisterHistory = (): void => kModalDialogLifecycle._RegisterHistory(this, kModalDialog); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogLifecycle._RegisterHistory carries the guard, try/catch and handler.
    private _OnDialogKeyDown = (ev: KeyboardEvent): void => kModalDialogLifecycle._OnDialogKeyDown(this, ev); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogLifecycle._OnDialogKeyDown carries the guard, try/catch and handler.
    private _IsTopMost = (): boolean => kModalDialogLifecycle._IsTopMost(this, kModalDialog); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogLifecycle._IsTopMost carries the guard, try/catch and handler.
    private _GetButtonRow = (): HTMLDivElement => kModalDialogButtons._GetButtonRow(this); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogButtons._GetButtonRow carries the guard, try/catch and handler.
    public AddCancelButton = (Callback?: any): void => kModalDialogButtons.AddCancelButton(this, Callback); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogButtons.AddCancelButton carries the guard, try/catch and handler.
    private _CancelClick = (): void => kModalDialogButtons._CancelClick(this); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogButtons._CancelClick carries the guard, try/catch and handler.
    public AddOKButton = (Callback?: any): void => kModalDialogButtons.AddOKButton(this, Callback); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogButtons.AddOKButton carries the guard, try/catch and handler.
    private _OKClick = (): void => kModalDialogButtons._OKClick(this); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogButtons._OKClick carries the guard, try/catch and handler.
    public SetTitle = (NewTitle:string): void => kModalDialogContent.SetTitle(this, NewTitle); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogContent.SetTitle carries the guard, try/catch and handler.
    public SetStandardAppTitle = (): void => kModalDialogContent.SetStandardAppTitle(this); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogContent.SetStandardAppTitle carries the guard, try/catch and handler.
    public CancelDialog = (): void => kModalDialogButtons.CancelDialog(this); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogButtons.CancelDialog carries the guard, try/catch and handler.
    /**
     * The MODERN title bar: a near-white bar with dark text and a 4px accent edge,
     * instead of SetGreyTitle's saturated colour block.
     *
     * WHY: a full-bleed high-chroma bar with white text is the single strongest
     * "old software" signal on a dialog, and it forces every title into low-contrast
     * white-on-colour. This keeps per-item colour coding - pass the item's colour and
     * it becomes a DOT beside the title plus the left edge - while the bar itself stays
     * neutral, so the title is plain dark-on-light text at full contrast.
     *
     * Additive: SetGreyTitle is untouched, so nothing changes for a caller that has not
     * moved over. `AccentColor` is optional; omitted, the bar uses the theme accent.
     */
    public SetAccentTitle = (AccentColor?: string): void => kModalDialogContent.SetAccentTitle(this, AccentColor); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogContent.SetAccentTitle carries the guard, try/catch and handler.

    /** Put the colour dot in front of the title text, replacing any earlier one so a
        re-titled dialog does not accumulate dots. */
    private _AddTitleDot = (Color: string): void => kModalDialogContent._AddTitleDot(this, Color); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogContent._AddTitleDot carries the guard, try/catch and handler.

    public SetGreyTitle = (): void => kModalDialogContent.SetGreyTitle(this); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogContent.SetGreyTitle carries the guard, try/catch and handler.
    public Close = (): void => kModalDialogLifecycle.Close(this, kModalDialog); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogLifecycle.Close carries the guard, try/catch and handler.
    private _ArmAutoRemove = (): void => kModalDialogLifecycle._ArmAutoRemove(this, kModalDialog); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogLifecycle._ArmAutoRemove carries the guard, try/catch and handler.
    private _CancelAutoRemove = (): void => kModalDialogLifecycle._CancelAutoRemove(this); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogLifecycle._CancelAutoRemove carries the guard, try/catch and handler.
    private _AutoRemove = (): void => kModalDialogLifecycle._AutoRemove(this); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogLifecycle._AutoRemove carries the guard, try/catch and handler.
    private _ReattachContainer = (): void => kModalDialogLifecycle._ReattachContainer(this); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogLifecycle._ReattachContainer carries the guard, try/catch and handler.
    /** Full teardown: every listener this instance put on `document` or on its own nodes is
     *  removed and the container is detached. Call it when the owning view is disposed —
     *  Close() alone leaves the container (and its backdrop listener) in the document, which
     *  is what leaks when a screen builds a fresh dialog per open. Safe to call more than
     *  once, and safe to call on a dialog that was never shown. */
    public Destroy = (): void => kModalDialogLifecycle.Destroy(this); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogLifecycle.Destroy carries the guard, try/catch and handler.
    /** Attribute a parked id is stashed under. Also the marker _RestoreIds looks for. */
    private static _ParkedIdAttr: string = "data-kparkedid";

    private _ParkIds = (): void => kModalDialogLifecycle._ParkIds(this, kModalDialog); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogLifecycle._ParkIds carries the guard, try/catch and handler.

    private _RestoreIds = (): void => kModalDialogLifecycle._RestoreIds(this, kModalDialog); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogLifecycle._RestoreIds carries the guard, try/catch and handler.

    private _DetachKeyListeners = (): void => kModalDialogLifecycle._DetachKeyListeners(this); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogLifecycle._DetachKeyListeners carries the guard, try/catch and handler.
    private _RemoveFromStack = (): void => kModalDialogLifecycle._RemoveFromStack(this, kModalDialog); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogLifecycle._RemoveFromStack carries the guard, try/catch and handler.
    private _RestoreFocus = (): void => kModalDialogLifecycle._RestoreFocus(this); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogLifecycle._RestoreFocus carries the guard, try/catch and handler.
    private _FocusableElements = (): HTMLElement[] => kModalDialogLifecycle._FocusableElements(this, kModalDialog); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogLifecycle._FocusableElements carries the guard, try/catch and handler.
    private _FocusFirstElement = (): void => kModalDialogLifecycle._FocusFirstElement(this); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogLifecycle._FocusFirstElement carries the guard, try/catch and handler.
    private _SetupFocusTrap = (): void => kModalDialogLifecycle._SetupFocusTrap(this); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogLifecycle._SetupFocusTrap carries the guard, try/catch and handler.
    private _OnTrapKeyDown = (ev: KeyboardEvent): void => kModalDialogLifecycle._OnTrapKeyDown(this, ev); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogLifecycle._OnTrapKeyDown carries the guard, try/catch and handler.
    /** Put a product's kScopedStyles scope (and optionally classes) on the dialog's CONTENT ROOT
     *  (InnerContent), so the product's scoped stylesheet reaches content it puts in the dialog.
     *
     *  WHY: a dialog is appended to <body>, OUTSIDE the app root that kScopedStyles.Apply stamped,
     *  so every `[data-app] .x` rule - including the reset and anything ScopeCSS produced - misses
     *  the dialog's content. Products worked around it by wrapping their content in a div carrying
     *  the attribute (DocumentBuilder's DOCBDialog). This does it once, here.
     *
     *  It goes on the content root, NOT the whole box: the title, the × and the OK/Cancel row keep
     *  the library's look, which the scoped reset (button, div, span rules) would otherwise change.
     *  `kScopedStyles.Apply` is used, so the reset stylesheet is injected even if the app root has
     *  not been rendered yet. Opt-in and additive: a dialog that never calls this is unchanged.
     *  Safe to call before or after Show(), and SetContent/SetContentElement keep it (they replace
     *  the root's children, not the root).
     *
     *  @param AppName   the name passed to kScopedStyles.Apply for the app root ("" = no attribute).
     *  @param ClassName optional space-separated classes for the content root. */
    public SetContentScope = (AppName: string, ClassName: string = ""): void => kModalDialogContent.SetContentScope(this, kModalDialog, AppName, ClassName); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogContent.SetContentScope carries the guard, try/catch and handler.
    public AddContent = (NewElem:HTMLElement ): void => kModalDialogContent.AddContent(this, NewElem); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogContent.AddContent carries the guard, try/catch and handler.
    /** Hand the dialog's APPEARANCE to a host stylesheet, so a product-branded app gets its own
     *  look instead of the built-in white box with the cyan title bar. Opt-in and additive: a
     *  caller that never calls this sees no change at all.
     *
     *  It is a method and not just a class-name field because adding a class is NOT enough. The
     *  constructor writes the chrome (background, border, radius, shadow, padding, and the
     *  backdrop colour) as INLINE styles, and an inline style beats any stylesheet rule short of
     *  `!important` — so a host class alone would lose every one of those properties and the app
     *  would be pushed into an `!important` war with this file. Clearing them first gives the
     *  host a clean canvas. The LAYOUT inline styles (fixed positioning, z-index, the flex column,
     *  overflow) are deliberately kept: they are what makes the dialog a dialog, not decoration.
     *
     *  Sizing follows the same principle. Width/Height default to an inline `80%`, which would
     *  equally beat a host rule, so while host-styled a Width/Height left at 0 means "the
     *  stylesheet decides" and no inline size is written. Setting either to a real number still
     *  wins, so one oversized dialog can opt back into an explicit pixel size.
     *
     *  @param ContentClassName   class(es) for the dialog BOX (space-separated, as in HTML).
     *  @param ContainerClassName class(es) for the full-screen BACKDROP. Optional. */
    public SetHostStyled = (ContentClassName: string, ContainerClassName?: string): void => kModalDialogContent.SetHostStyled(this, kModalDialog, ContentClassName, ContainerClassName); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogContent.SetHostStyled carries the guard, try/catch and handler.
    private static _AddClasses = (Target: HTMLElement, ClassNames: string): void => kModalDialogStatics._AddClasses(Target, ClassNames); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogStatics._AddClasses carries the guard, try/catch and handler.
    public SetWidth = (): void => kModalDialogContent.SetWidth(this); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogContent.SetWidth carries the guard, try/catch and handler.

    public SetHeigth = (): void => kModalDialogContent.SetHeigth(this); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogContent.SetHeigth carries the guard, try/catch and handler.
    /** HTML-string content path. Fast, BUT every `addEventListener`
        wired before this call is discarded by the `.innerHTML =`
        assignment. Use this only for read-only key/value dialogs
        (ListDialog, ShowModalMessage). For any modal with buttons,
        inputs, radio groups, or sub-editors, use `SetContentElement`
        below so listeners on the DOM children survive the mount.
        See shared skill `rich-kmodaldialog` for the live pattern. */
    public SetContent = (NewHTML:string): void => kModalDialogContent.SetContent(this, NewHTML); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogContent.SetContent carries the guard, try/catch and handler.
    /** DOM-element content path — `addEventListener` calls on the
        passed node's children are preserved. Pair with
        `setTimeout(callback, 0)` after `Show()` if any child widget
        (people-picker, etc.) mounts lazily on the next frame and the
        wiring needs the child to exist first. */
    public SetContentElement = (NewContent:Element): void => kModalDialogContent.SetContentElement(this, NewContent); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogContent.SetContentElement carries the guard, try/catch and handler.
    public SetCancelButton = (): void => kModalDialogButtons.SetCancelButton(this); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogButtons.SetCancelButton carries the guard, try/catch and handler.

    /**
     * Caption the Cancel button, whether or not it is built yet. Setting CancelButtonCaption alone is
     * too late once the constructor has built the button (showCancelButton=true): Show() ->
     * AddCancelButton returns early for an existing button, so the caption was never applied
     * (DocumentBuilder BUG-34). The click handler is left as it is (SetCancelButton would replace it).
     */
    public SetCancelCaption = (Caption: string): void => kModalDialogButtons.SetCancelCaption(this, Caption); // DOCUMENTED EXCEPTION: one-line delegate; kModalDialogButtons.SetCancelCaption carries the guard, try/catch and handler.
}
